/**
 * AIPromptsEditor Component
 *
 * Settings panel for editing AI prompt templates.
 * Displays each prompt as an editable card with system prompt
 * and user prompt template fields.
 * Changes are saved to the server and persist across sessions.
 */

import { useState, useCallback, useEffect } from 'react';
import { useAIPrompts } from '@/hooks/useAIPrompts';
import type { EffectivePrompt } from '@/hooks/useAIPrompts';
import './AIPromptsEditor.css';

const VARIABLE_DOCS = [
  { name: '{{trendBlock}}', desc: 'Monthly KPI data rows (revenue, GM%, DLER, CCC, runway, rev/FTE)' },
  { name: '{{detailBlock}}', desc: 'Monthly P&L detail (net income, cash balance, burn rate, direct labour)' },
  { name: '{{eventsBlock}}', desc: 'Context events annotated on the trend charts' },
  { name: '{{commentsBlock}}', desc: 'User comments from the dashboard (up to 50 most recent)' },
];

export function AIPromptsEditor() {
  const { prompts, loading, error, saving, saveOverride, resetOverride } = useAIPrompts();

  if (loading) {
    return (
      <div className="ape-loading">
        <div className="ape-skeleton" />
        <div className="ape-skeleton" style={{ height: 120, marginTop: 16 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ape-error">
        <p>Failed to load prompt settings: {error}</p>
      </div>
    );
  }

  return (
    <div className="ape">
      <div className="ape-header">
        <h2 className="ape-title">AI Prompt Settings</h2>
        <p className="ape-subtitle">
          Customise the prompts sent to Claude for each AI feature.
          Changes take effect immediately — the 24-hour insight cache will use the new prompt on next refresh.
        </p>
      </div>

      <div className="ape-variables">
        <h3 className="ape-variables__title">Available Template Variables</h3>
        <div className="ape-variables__grid">
          {VARIABLE_DOCS.map((v) => (
            <div key={v.name} className="ape-variable">
              <code className="ape-variable__name">{v.name}</code>
              <span className="ape-variable__desc">{v.desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="ape-cards">
        {prompts.map((prompt) => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            isSaving={saving === prompt.id}
            onSave={saveOverride}
            onReset={resetOverride}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PromptCard — individual editable prompt
// ---------------------------------------------------------------------------

interface PromptCardProps {
  prompt: EffectivePrompt;
  isSaving: boolean;
  onSave: (id: string, data: { systemPrompt?: string; userPromptTemplate?: string }) => Promise<void>;
  onReset: (id: string) => Promise<void>;
}

function PromptCard({ prompt, isSaving, onSave, onReset }: PromptCardProps) {
  const [systemPrompt, setSystemPrompt] = useState(prompt.systemPrompt);
  const [userPromptTemplate, setUserPromptTemplate] = useState(prompt.userPromptTemplate);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Sync local state when the prompt prop changes (e.g. after Reset to default)
  useEffect(() => {
    setSystemPrompt(prompt.systemPrompt);
    setUserPromptTemplate(prompt.userPromptTemplate);
  }, [prompt.systemPrompt, prompt.userPromptTemplate]);

  const isDirty = systemPrompt !== prompt.systemPrompt || userPromptTemplate !== prompt.userPromptTemplate;

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaved(false);
    try {
      await onSave(prompt.id, { systemPrompt, userPromptTemplate });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveError('Save failed — try again');
    }
  }, [prompt.id, systemPrompt, userPromptTemplate, onSave]);

  const handleReset = useCallback(async () => {
    setSaveError(null);
    setSaved(false);
    try {
      await onReset(prompt.id);
    } catch {
      setSaveError('Reset failed — try again');
    }
  }, [prompt.id, onReset]);

  return (
    <div className={`ape-card${prompt.isOverridden ? ' ape-card--overridden' : ''}`}>
      <div className="ape-card__header">
        <div className="ape-card__meta">
          <h3 className="ape-card__title">{prompt.label}</h3>
          <p className="ape-card__desc">{prompt.description}</p>
        </div>
        {prompt.isOverridden && (
          <span className="ape-card__badge">Custom</span>
        )}
      </div>

      <div className="ape-card__fields">
        <label className="ape-label">
          System Prompt
          <textarea
            className="ape-textarea ape-textarea--system"
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={10}
            spellCheck={false}
          />
        </label>

        <label className="ape-label">
          User Prompt Template
          <textarea
            className="ape-textarea ape-textarea--user"
            value={userPromptTemplate}
            onChange={(e) => setUserPromptTemplate(e.target.value)}
            rows={14}
            spellCheck={false}
          />
        </label>
      </div>

      {saveError && <p className="ape-card__error">{saveError}</p>}

      <div className="ape-card__actions">
        {prompt.isOverridden && (
          <button
            className="ape-btn ape-btn--ghost"
            onClick={() => void handleReset()}
            disabled={isSaving}
            title="Revert to hardcoded default"
          >
            Reset to default
          </button>
        )}
        <button
          className={`ape-btn ape-btn--primary${saved ? ' ape-btn--saved' : ''}`}
          onClick={() => void handleSave()}
          disabled={isSaving || !isDirty}
        >
          {isSaving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
